import { useParams, useNavigate } from 'react-router-dom'
import { useLedger } from '../api/hooks.ts'
import { Card, ErrorNote, Screen, Spinner } from '../components/ui.tsx'
import { eur, eurSigned } from '../lib/format.ts'

const LABELS: Record<string, string> = {
  DEDUCTION: 'Tarea no hecha',
  BONUS: 'Bonus aprobado',
  DEDUCTION_CAP: 'Tope de deducción',
  BONUS_CAP: 'Techo de bonus',
  DEDUCTION_REVERSAL: 'Descuento devuelto',
  BONUS_SPLIT: 'Mitad para tu hermano',
  MANUAL_ADJUST: 'Ajuste manual',
}

/**
 * Motivos que escribe el propio servidor cuando nadie ha explicado nada
 * ("Bonus: Ordenar el garaje"). Repiten el título que ya encabeza la fila, así
 * que se omiten.
 */
const AUTO_REASONS = ['No hecha', 'Bonus', 'Bonus a medias', 'Mitad cedida']

/**
 * Un motivo escrito por una persona SIEMPRE se muestra: es la única
 * explicación que tiene el hijo de por qué le falta ese dinero. Antes se
 * ocultaba en cuanto mencionaba el título de la tarea, y con una tarea llamada
 * «Castigo» eso borraba justo el texto que había que leer.
 */
function subtitleFor(entry: { type: string; reason: string | null; taskTitle: string | null }): string {
  const label = LABELS[entry.type] ?? entry.type
  const reason = entry.reason ?? ''
  if (reason === '') return label
  if (!entry.taskTitle) return reason

  const auto = AUTO_REASONS.some((prefix) => reason === `${prefix}: ${entry.taskTitle}`)
  return auto ? label : `${label} · ${reason}`
}

/**
 * El desglose céntimo a céntimo.
 *
 * Con el modelo por excepción el hijo no participa en el registro de lo que
 * hace, así que sólo puede confiar en lo que ve aquí. La lista está construida
 * para poder sumarse a mano: asignación base, cada movimiento con su signo, y
 * el total. Si eso no cuadrara, el sistema entero pierde legitimidad.
 */
export function LedgerScreen() {
  const { weekId } = useParams<{ weekId: string }>()
  const navigate = useNavigate()
  const ledger = useLedger(weekId)

  if (ledger.isLoading) return <Spinner />
  if (ledger.error) return <Screen><ErrorNote error={ledger.error} /></Screen>
  if (!ledger.data) return null

  const { baseAmountCents, entries, totalCents, payoutCents, status } = ledger.data

  return (
    <Screen>
      <button onClick={() => navigate(-1)} className="mb-3 text-sm text-[var(--color-brand)]">
        ← Volver
      </button>
      <h1 className="mb-1 text-xl font-bold tracking-tight">Desglose</h1>
      <p className="mb-4 text-sm text-[var(--color-muted)]">
        {status === 'OPEN' ? 'Semana en curso' : 'Semana cerrada'}
      </p>

      <Card>
        <div className="flex items-center justify-between border-b border-[var(--color-line)] py-3">
          <span className="text-[15px] font-medium">Asignación semanal</span>
          <span className="tabular-nums font-semibold">{eur(baseAmountCents)}</span>
        </div>

        {entries.length === 0 && (
          <p className="py-4 text-center text-sm text-[var(--color-muted)]">
            Ningún movimiento todavía.
          </p>
        )}

        {entries.map((entry) => (
          <div
            key={entry.id}
            className="flex items-start justify-between gap-3 border-b border-[var(--color-line)] py-3 last:border-b-0"
          >
            <div className="min-w-0">
              <p className="text-[15px]">{entry.taskTitle ?? LABELS[entry.type] ?? entry.type}</p>
              <p className="text-xs text-[var(--color-muted)]">{subtitleFor(entry)}</p>
            </div>
            <span
              className={`shrink-0 tabular-nums font-medium ${
                entry.amountCents < 0 ? 'text-[var(--color-bad)]' : 'text-[var(--color-good)]'
              }`}
            >
              {eurSigned(entry.amountCents)}
            </span>
          </div>
        ))}

        <div className="mt-2 flex items-center justify-between border-t-2 border-[var(--color-line)] pt-3">
          <span className="font-semibold">
            {status === 'OPEN' ? 'Llevas ahora' : 'Total cobrado'}
          </span>
          <span className="text-xl font-bold tabular-nums">{eur(payoutCents ?? totalCents)}</span>
        </div>
      </Card>

      <p className="mt-4 text-center text-xs text-[var(--color-muted)]">
        Asignación {eur(baseAmountCents)} + movimientos = {eur(totalCents)}
      </p>
    </Screen>
  )
}
